import { useQuery } from '@tanstack/react-query';
import type { GetStaticProps, InferGetStaticPropsType } from 'next';
import { lazy, Suspense } from 'react';

import { getRegister } from 'services/idm';

import { apiErrorHandler } from '@login/helpers';
import { useApiRedirect } from '@login/hooks/useApiRedirect';

const RegisterForm = lazy(
	() => import('@login/patterns/register-form/register-form')
);

type PageProps = {
	locale?: string;
};

export const getStaticProps = (async (params) => {
	const locale = params?.locale as string;
	return { props: { locale } };
}) satisfies GetStaticProps<PageProps>;

const Register = ({
	locale,
}: InferGetStaticPropsType<typeof getStaticProps>) => {
	const { goToRedirect } = useApiRedirect();

	const { data: registerData } = useQuery({
		queryKey: ['register', locale],
		queryFn: () =>
			getRegister(locale).catch((error) =>
				apiErrorHandler(error, locale)
			),
		enabled: !!locale,
		throwOnError: true,
	});

	if (registerData) {
		goToRedirect({
			action: registerData.action,
			url: registerData.redirectUrl,
			isConnect: true,
		});
	}

	if (registerData && registerData.action === 'Display') {
		return (
			<Suspense fallback={null}>
				<RegisterForm
					urls={{
						registerUrl: registerData.placeholders?.registerUrl,
						signInUrl: registerData.placeholders?.signInUrl,
						forgottenPasswordUrl:
							registerData.placeholders?.forgottenPasswordUrl,
						privacyPolicyUrl:
							registerData.placeholders?.privacyPolicyUrl,
						supportName: registerData.placeholders?.supportName,
					}}
					marketingMessage={registerData.marketingMessage}
					disclaimerMessage={registerData.disclaimerMessage}
					token={registerData?.xsrfToken}
					minimumAge={registerData.minimumAge}
				/>
			</Suspense>
		);
	}

	return null;
};

export default Register;
